import { useState } from 'react';
import { DataTable } from '@/components/records/DataTable';
import { RecordDetailsModal } from '@/components/records/RecordDetailsModal';
import { useLanguage } from '@/context/LanguageContext';
import { useRecords } from '@/hooks/useRecords';
import { useFieldVisibility } from '@/context/FieldVisibilityContext';
import { useTable } from '@/context/TableContext';
import type { CallRecord } from '@/types';

import * as XLSX from 'xlsx';
import { formatDateTime, formatDuration, getStatusLabel } from '@/utils';

interface RecordsPageProps {
  tableName?: string;
}

export function RecordsPage({ tableName = 'alsalama_call_records' }: RecordsPageProps) {
  const { t, language } = useLanguage();
  const { getVisibleFields } = useFieldVisibility();
  const { tableColumns, currentTable } = useTable();
  const [selectedRecord, setSelectedRecord] = useState<Record<string, unknown> | null>(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'warning' } | null>(null);

  // Show toast message
  const showToast = (type: 'success' | 'warning', messageKey: string) => {
    setToast({ type, message: t(messageKey) });
    setTimeout(() => setToast(null), 3000);
  };

  const {
    records,
    loading,
    error,
    pagination,
    filterState,
    sortState,
    categories,
    serviceTypes,
    setFilterState,
    setSortState,
    setPagination,
    toggleStatus,
  } = useRecords({
    tableName,
    pageSize: 25,
    enableRealtime: true,
  });

  // View record details
  const handleView = (record: Record<string, unknown>) => {
    setSelectedRecord(record);
    setViewDialogOpen(true);
  };

  // Handle status toggle
  const handleStatusToggle = async (id: string, status: boolean) => {
    try {
      await toggleStatus(id, status);
      // Show success/warning message
      if (status) {
        showToast('success', 'status.resolvedMessage');
      } else {
        showToast('warning', 'status.pendingMessage');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred';
      console.error('Failed to toggle status:', err);
      console.error('Error details:', errorMessage);
    }
  };

  // Get field label
  const getFieldLabel = (key: string): string => {
    const labelKeys: Record<string, string> = {
      id: 'field.id',
      created_at: 'field.date',
      name: 'field.name',
      phone_number: 'field.phone',
      category: 'field.category',
      service_type: 'field.service',
      inquiry: 'field.inquiry',
      call_duration_sec: 'field.duration',
      status: 'field.status',
      urgency: 'field.urgency',
      notes: 'field.notes',
      call_summary: 'field.summary',
      conversation_id: 'field.conversationId',
      audio_uri: 'field.audio',
    };
    return t(labelKeys[key] || key);
  };

  // Format field value for export
  const formatFieldValue = (record: CallRecord, key: string): string => {
    const value = record[key as keyof CallRecord];

    switch (key) {
      case 'created_at':
        return formatDateTime(value as string, language);
      case 'call_duration_sec':
        return formatDuration(value as number | null);
      case 'status':
        const status = getStatusLabel(value as boolean | null);
        return status.label;
      case 'urgency':
        // Format urgency level for display using translations
        const urgencyKeys: Record<string, string> = {
          low: 'urgency.low',
          medium: 'urgency.medium',
          high: 'urgency.high',
          extra_high: 'urgency.extraHigh',
          no: 'urgency.no',
        };
        return t(urgencyKeys[value as string] || (value as string) || '');
      case 'category':
      case 'service_type':
        return value as string || '-';
      case 'inquiry':
      case 'notes':
      case 'call_summary':
        // Truncate long text for PDF
        const text = value as string || '';
        return text.length > 100 ? text.substring(0, 97) + '...' : text;
      case 'name':
        return value as string || '-';
      case 'phone_number':
        return value as string || '-';
      case 'conversation_id':
      case 'audio_uri':
        return value as string || '-';
      case 'id':
        return String(value) || '-';
      default:
        return value as string || '';
    }
  };

  // Export to CSV - use dynamic columns
  const handleExportCSV = () => {
    // For alsalama_call_records table, use visibility settings. For other tables, use all columns if no visibility config exists
    const visibleCsvFields = getVisibleFields('csv');
    const exportColumns = currentTable === 'alsalama_call_records'
      ? visibleCsvFields.filter(col => col !== 'id')
      : (tableColumns.length > 0 && visibleCsvFields.length > 0
          ? tableColumns.filter(col => col !== 'id').filter(col => visibleCsvFields.includes(col))
          : tableColumns.filter(col => col !== 'id'));

    // Build header row
    const headers = exportColumns.map(key => {
      // Format column name nicely for dynamic columns
      if (tableColumns.length > 0) {
        return key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
      }
      return getFieldLabel(key);
    });

    // Build data rows
    const data = records.map(record => {
      return exportColumns.map(key => {
        const value = record[key as keyof CallRecord];
        if (value === null || value === undefined) return '';
        // Format specific fields for alsalama_call_records
        if (tableColumns.length === 0) {
          return formatFieldValue(record, key);
        }
        return String(value);
      });
    });

    // Create CSV content
    const csvContent = [
      headers.join(','),
      ...data.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
    ].join('\n');

    // Download file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `${tableName}_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Export to Excel - use dynamic columns
  const handleExportExcel = () => {
    // For alsalama_call_records table, use visibility settings. For other tables, use all columns if no visibility config exists
    const visiblePdfFields = getVisibleFields('pdf');
    const exportColumns = currentTable === 'alsalama_call_records'
      ? visiblePdfFields.filter(col => col !== 'id')
      : (tableColumns.length > 0 && visiblePdfFields.length > 0
          ? tableColumns.filter(col => col !== 'id').filter(col => visiblePdfFields.includes(col))
          : tableColumns.filter(col => col !== 'id'));

    // Build header row
    const headers = exportColumns.map(key => {
      // Format column name nicely for dynamic columns
      if (tableColumns.length > 0) {
        return key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
      }
      return getFieldLabel(key);
    });

    // Build data rows
    const data = records.map(record => {
      return exportColumns.map(key => {
        const value = record[key as keyof CallRecord];
        if (value === null || value === undefined) return '';
        // Format specific fields for alsalama_call_records
        if (tableColumns.length === 0) {
          return formatFieldValue(record, key);
        }
        return String(value);
      });
    });

    // Create worksheet data with headers
    const worksheetData = [headers, ...data];

    // Create workbook and worksheet
    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);

    // Set column widths for better readability
    const columnWidths = exportColumns.map(key => {
      const headerLength = headers[exportColumns.indexOf(key)].length;
      // Calculate max width based on data
      const maxDataLength = Math.max(
        ...records.map(record => {
          const value = record[key as keyof CallRecord];
          return value ? String(value).length : 0;
        })
      );
      return { wch: Math.max(headerLength, maxDataLength, 10) + 2 };
    });
    worksheet['!cols'] = columnWidths;

    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Records');

    // Generate filename with date
    const fileName = `${tableName}_${new Date().toISOString().split('T')[0]}.xlsx`;

    // Download file
    XLSX.writeFile(workbook, fileName);
  };

  return (
    <div className="space-y-6">
      {/* Toast Notification - Bottom Right */}
      {toast && (
        <div className={`fixed bottom-4 right-4 z-50 px-4 py-3 rounded-lg shadow-lg flex items-center gap-2 animate-fade-in ${
          toast.type === 'success' 
            ? 'bg-green-600 text-white' 
            : 'bg-amber-500 text-white'
        }`}>
          {toast.type === 'success' ? (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          ) : (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          )}
          <span className="font-medium">{toast.message}</span>
        </div>
      )}

      {/* Error Alert */}
      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-xl">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}

      {/* Quick Filter Bar */}
      <div className="bg-card rounded-xl border p-4">
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
          <span className="text-sm font-semibold text-foreground">{t('quickFilter.quick')}</span>
          <div className="flex flex-wrap gap-2">
            {(['all', 'today', 'yesterday', 'thisWeek', 'lastWeek', 'thisMonth', 'lastMonth'] as const).map((preset) => (
              <button
                key={preset}
                onClick={() => setFilterState({ dateFilter: { preset, from: null, to: null } })}
                className={`px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200 ${
                  filterState.dateFilter.preset === preset
                    ? 'bg-primary text-primary-foreground shadow-sm'
                    : 'bg-muted hover:bg-muted/80 text-muted-foreground'
                }`}
              >
                {t(`filter.${preset}`)}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Action Bar */}
      <div className="flex justify-between items-center">
        <div></div>
      </div>

      {/* Data Table */}
      <DataTable
        records={records}
        loading={loading}
        pagination={pagination}
        onPaginationChange={(page, pageSize) => setPagination({ page, pageSize })}
        onSort={(column) => setSortState({
          column,
          direction: sortState.column === column && sortState.direction === 'asc' ? 'desc' : 'asc',
        })}
        sortState={sortState}
        filterState={filterState}
        onFilterChange={setFilterState}
        onView={handleView as unknown as (record: CallRecord) => void}
        onStatusToggle={handleStatusToggle}
        onExportCSV={handleExportCSV}
        onExportExcel={handleExportExcel}
        categories={categories}
        serviceTypes={serviceTypes}
        columns={tableColumns}
      />

      {/* View Record Dialog */}
      <RecordDetailsModal
        isOpen={viewDialogOpen}
        onClose={() => setViewDialogOpen(false)}
        record={selectedRecord}
        columns={tableColumns}
        title={t('modal.recordDetails')}
        onStatusChange={async (id, newStatus) => {
          try {
            await toggleStatus(id, newStatus);
            // Show success/warning message
            if (newStatus) {
              showToast('success', 'status.resolvedMessage');
            } else {
              showToast('warning', 'status.pendingMessage');
            }
          } catch (error) {
            console.error('Failed to update status:', error);
          }
        }}
      />
    </div>
  );
}
